import { ArrowLeftIcon, CheckIcon, XIcon } from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import { useApproveReservation } from '@/api/reservations/approve-reservation'
import { useCancelReservation } from '@/api/reservations/cancel-reservation'
import { useChangeReservationLocation } from '@/api/reservations/change-reservation-location'
import { useCreateReservationNote } from '@/api/reservations/create-reservation-note'
import { useRemoveReservationNote } from '@/api/reservations/delete-reservation-note'
import {
  ReservationStatus,
  ReservationStatusEnum,
} from '@/api/reservations/get-reservation-dashboard'
import { useGetReservationDetails } from '@/api/reservations/get-reservation-details'
import { useGetReservationNotes } from '@/api/reservations/get-reservation-notes'
import { useGetReservationTransactions } from '@/api/reservations/get-reservation-transactions'
import { useUpdateReservationNote } from '@/api/reservations/update-reservation-note'
import { Page } from '@/components/layout/page'
import { PageHeader } from '@/components/layout/page-header'
import * as HeaderS from '@/components/layout/page-header/page-header.styled'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Icon } from '@/components/ui/icon'
import { Link } from '@/components/ui/link'
import { NavbarMenu, NavbarMenuItem } from '@/components/ui/navbar-menu'
import { popupFactory, routerFactory } from '@/factories'
import { useSearchParams } from '@/hooks'

import { DeclineReservationConfirmationPopup } from '../reservations-dashboard/decline-reservation-confirmation-popup'
import {
  getStatusLabel,
  RefundState,
  STATUS_COLOR_MAP,
} from '../reservations-dashboard/reservations-dashboard.constants'
import { Header } from './Header'
import {
  ReservationDetailsTabsEnum,
  reservationDetailsQueryParams,
  reservationDetailsQueryParamsSchema,
} from './reservation-details.constants'
import { getReservationTabCount } from './reservation-details.utils'
import * as S from './reservations-details.styled'
import { CustomQuestions } from './tabs/CustomQuestion'
import { Details } from './tabs/Details'
import { Documents } from './tabs/Documents'
import { Notes } from './tabs/Notes'
import { Payments } from './tabs/Payments'

export const ReservationDetailsPage = () => {
  const { t } = useTranslation(['reservations'])
  const { reservationId } = useParams<{ reservationId: string }>()
  const { Navigate } = routerFactory.useRouter()

  const {
    data: reservationDetails,
    isLoading: isReservationDetailsLoading,
    error: reservationDetailsError,
  } = useGetReservationDetails(reservationId || '')
  const { data: reservationTransactions, isLoading: isReservationTransactionsLoading } =
    useGetReservationTransactions(reservationId || '')
  const { data: reservationNotes, isLoading: isReservationNotesLoading } = useGetReservationNotes(
    reservationId || '',
  )
  const { mutateAsync: changeReservationLocation, isPending: isChangeReservationLocationPending } =
    useChangeReservationLocation()
  const { mutateAsync: approveReservation, isPending: isApproveReservationPending } =
    useApproveReservation()
  const { mutateAsync: cancelReservation, isPending: isCancelReservationPending } =
    useCancelReservation()
  const { mutateAsync: createReservationNote, isPending: isCreateReservationNotePending } =
    useCreateReservationNote()
  const { mutateAsync: updateReservationNote, isPending: isUpdateReservationNotePending } =
    useUpdateReservationNote()
  const { mutateAsync: removeReservationNote, isPending: isRemoveReservationNotePending } =
    useRemoveReservationNote()

  const searchParams = useSearchParams({
    initialState: reservationDetailsQueryParams,
    schema: reservationDetailsQueryParamsSchema,
  })

  const shouldRedirect =
    reservationDetailsError?.response?.status &&
    [403, 404].includes(reservationDetailsError?.response?.status)

  const activeTab = searchParams.parsedQuery.tab
  const buttonDisabled =
    isReservationDetailsLoading ||
    isReservationTransactionsLoading ||
    isReservationNotesLoading ||
    isChangeReservationLocationPending ||
    isApproveReservationPending ||
    isCancelReservationPending ||
    isCreateReservationNotePending ||
    isUpdateReservationNotePending ||
    isRemoveReservationNotePending

  const cancellableReservationStatuses: ReservationStatus[] = [
    ReservationStatusEnum.enum.approved,
    ReservationStatusEnum.enum.ongoing,
    ReservationStatusEnum.enum.completed,
  ]

  const reservationDetailsTabsOptions = ReservationDetailsTabsEnum.options
    .filter((tab) => {
      switch (tab) {
        case ReservationDetailsTabsEnum.enum['custom-questions']:
          return (reservationDetails?.questions?.length ?? 0) > 0

        case ReservationDetailsTabsEnum.enum.payments:
          return (reservationTransactions?.transactions.length || 0) > 0

        case ReservationDetailsTabsEnum.enum.documents:
          return !!reservationDetails?.signedAgreementFile

        default:
          return true
      }
    })
    .map((value) => ({
      active: activeTab === value,
      count: getReservationTabCount({
        customQuestionsCount: reservationDetails?.questions?.length,
        documentsCount: reservationDetails?.signedAgreementFile ? 1 : 0,
        reservationNotesCount: reservationNotes?.length,
        reservationTransactionsCount: reservationTransactions?.transactions.length,
        tab: value,
      }),
      label: t(`reservations:details.tabs.${value}`),
      onClick: () => searchParams.writeParams({ tab: value }),
    }))

  const onDeclineReservation = (allowPartialRefund?: boolean) => {
    if (!reservationDetails) {
      return
    }

    popupFactory.open(DeclineReservationConfirmationPopup, {
      allowPartialRefund,
      reservation: reservationDetails,
    })
  }

  const onCancelReservationConfirm = async ({
    refundState,
    refundAmount,
    cancelNote,
  }: {
    refundState: RefundState
    refundAmount?: string
    cancelNote: string
  }) => {
    if (!reservationId) {
      return
    }

    await cancelReservation({
      cancelNote,
      refundAmount,
      refundState,
      reservationId,
    })
  }

  const onCancelReservation = (allowPartialRefund?: boolean) => {
    if (!reservationDetails) {
      return
    }

    popupFactory.open(DeclineReservationConfirmationPopup, {
      allowPartialRefund,
      isFree: !reservationDetails?.amenityId.isPaid,
      onConfirm: onCancelReservationConfirm,
      reservation: reservationDetails,
    })
  }

  if (shouldRedirect) {
    return <Navigate to={{ path: '/dashboard/not-found' }} />
  }

  return (
    <>
      <Page>
        <S.Container>
          <S.ReservationDetailsPageHeader>
            <PageHeader
              loading={buttonDisabled}
              title={t('reservations:details.header.reservationNumber', {
                reservationId: reservationId || '',
              })}
              backButton={
                <Link
                  navigateBack
                  size="small"
                  use="secondary"
                  to={{
                    path: '/reservations/bookings',
                  }}
                >
                  <Icon icon={ArrowLeftIcon} size="1.4rem" />
                  Back
                </Link>
              }
              badges={
                reservationDetails?.status && (
                  <Badge
                    color={STATUS_COLOR_MAP[reservationDetails?.status]}
                    size="small"
                    variant="light"
                  >
                    {getStatusLabel(t, reservationDetails?.status)}
                  </Badge>
                )
              }
              actions={
                <>
                  {reservationDetails?.status === 'pending' && (
                    <>
                      <Button
                        size="small"
                        use="danger"
                        disabled={buttonDisabled}
                        onClick={() => onDeclineReservation()}
                      >
                        <Icon icon={XIcon} size="1.4rem" />
                        {t('reservations:details.header.decline')}
                      </Button>
                      <Button
                        size="small"
                        use="primary"
                        disabled={buttonDisabled}
                        onClick={async () =>
                          await approveReservation({ reservationId: reservationDetails?.id })
                        }
                      >
                        {t('reservations:details.header.approve')}
                        <Icon icon={CheckIcon} size="1.4rem" />
                      </Button>
                    </>
                  )}
                  {reservationDetails?.status &&
                    cancellableReservationStatuses.includes(reservationDetails.status) && (
                      <Button
                        size="small"
                        use="danger"
                        disabled={buttonDisabled}
                        onClick={() => onCancelReservation(true)}
                      >
                        <Icon icon={XIcon} size="1.4rem" />
                        {t('reservations:details.header.cancelReservation')}
                      </Button>
                    )}
                </>
              }
            />
          </S.ReservationDetailsPageHeader>

          <Header reservation={reservationDetails} />

          <NavbarMenu>
            {reservationDetailsTabsOptions.map((option) => {
              return (
                <NavbarMenuItem key={option.label} active={option.active} onClick={option.onClick}>
                  {option.label}
                  {option.count !== undefined && (
                    <HeaderS.NavbarItemCountContainer>
                      {option.count}
                    </HeaderS.NavbarItemCountContainer>
                  )}
                </NavbarMenuItem>
              )
            })}
          </NavbarMenu>

          {activeTab === ReservationDetailsTabsEnum.enum.details && (
            <Details
              reservation={reservationDetails}
              isLoading={buttonDisabled}
              onLocationChange={changeReservationLocation}
            />
          )}
          {activeTab === ReservationDetailsTabsEnum.enum.payments && (
            <Payments
              paymentsInfo={reservationTransactions}
              isLoading={isReservationTransactionsLoading}
            />
          )}
          {activeTab === ReservationDetailsTabsEnum.enum['custom-questions'] && (
            <CustomQuestions
              questions={reservationDetails?.questions}
              isLoading={isReservationDetailsLoading}
            />
          )}
          {activeTab === ReservationDetailsTabsEnum.enum.documents && (
            <Documents
              agreement={reservationDetails?.signedAgreementFile}
              isLoading={isReservationDetailsLoading}
            />
          )}
          {activeTab === ReservationDetailsTabsEnum.enum.notes && (
            <Notes
              notes={reservationNotes}
              reservationId={reservationId}
              isLoading={buttonDisabled}
              onCreate={createReservationNote}
              onUpdate={updateReservationNote}
              onRemove={removeReservationNote}
            />
          )}
        </S.Container>
      </Page>
    </>
  )
}
